import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Paper, TextField, Button,
    Alert, Select, MenuItem, FormControl, CircularProgress,
    IconButton
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AdminScripts = () => {
    const { t } = useTranslation();
    const { currentUser, api } = useAuth();
    const navigate = useNavigate();

    const [scripts, setScripts] = useState([]);
    const [selectedScript, setSelectedScript] = useState('');
    const [inputJson, setInputJson] = useState('{\n  \n}');
    const [jsonError, setJsonError] = useState('');
    const [executing, setExecuting] = useState(false);
    const [executionResult, setExecutionResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (currentUser && !currentUser.is_super_admin) {
            navigate('/');
            return;
        }

        const fetchScripts = async () => {
            try {
                const response = await api.get('/admin-scripts/');
                setScripts(response.data);
                if (response.data.length > 0) {
                    const firstScript = response.data[0];
                    setSelectedScript(firstScript.name);
                    setInputJson(JSON.stringify(firstScript.sample_input, null, 2));
                }
            } catch (err) {
                console.error("Failed to fetch scripts", err);
                setError(t("Could not load scripts from the server."));
            } finally {
                setLoading(false);
            }
        };

        if (currentUser?.is_super_admin) {
            fetchScripts();
        }
    }, [currentUser, api, navigate, t]);

    const handleScriptChange = (e) => {
        const scriptName = e.target.value;
        setSelectedScript(scriptName);
        
        const script = scripts.find(s => s.name === scriptName);
        if (script) {
            setInputJson(JSON.stringify(script.sample_input, null, 2));
        }
        setJsonError('');
    };

    const handleJsonChange = (e) => {
        setInputJson(e.target.value);
        if (jsonError) setJsonError('');
    };

    const handleExecute = async () => {
        if (!selectedScript) return;

        // Front-end JSON validation
        let parsedJson;
        try {
            parsedJson = JSON.parse(inputJson);
        } catch (e) {
            setJsonError(`${t('Invalid JSON format:')} ${e.message}`);
            return;
        }

        setExecuting(true);
        setExecutionResult(null);
        setError(null);

        try {
            const response = await api.post(`/admin-scripts/${encodeURIComponent(selectedScript)}/execute`, {
                input_data: parsedJson
            });
            setExecutionResult(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || t("An error occurred during execution."));
        } finally {
            setExecuting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!currentUser?.is_super_admin) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">{t('You do not have permission to access this page.')}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/admin')} sx={{ mr: { xs: 1, sm: 2 } }} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {t('Admin Scripts')}
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        {t('Select Script')}
                    </Typography>
                    <FormControl fullWidth>
                        <Select
                            id="script-select"
                            value={selectedScript}
                            onChange={handleScriptChange}
                            displayEmpty
                        >
                            {scripts.length === 0 && <MenuItem disabled value="">{t('No scripts available')}</MenuItem>}
                            {scripts.map((s) => (
                                <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        {t('Input JSON')}
                    </Typography>
                    <TextField
                        multiline
                        rows={10}
                        value={inputJson}
                        onChange={handleJsonChange}
                        error={!!jsonError}
                        helperText={jsonError}
                        fullWidth
                        sx={{ fontFamily: 'monospace' }}
                        InputProps={{
                            sx: { fontFamily: 'monospace', fontSize: '0.9rem' }
                        }}
                    />
                </Box>

                <Button
                    variant="contained"
                    size="large"
                    startIcon={executing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                    onClick={handleExecute}
                    disabled={executing || !selectedScript}
                    sx={{ px: 4 }}
                >
                    {executing ? t('Executing...') : t('Execute Script')}
                </Button>
            </Paper>

            {executionResult && (
                <Paper sx={{ p: 0, overflow: 'hidden', border: '1px solid', borderColor: executionResult.status === 'error' ? 'error.main' : 'success.main' }}>
                    <Box sx={{ p: 2, bgcolor: executionResult.status === 'error' ? 'error.light' : 'success.light', color: 'white', display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {executionResult.status === 'error' ? t('Execution Error') : t('Execution Successful')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        {executionResult.error && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>{t('Error Details:')}</Typography>
                                <Paper elevation={0} sx={{ bgcolor: 'grey.100', p: 2, overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                        {executionResult.error}
                                    </pre>
                                </Paper>
                            </Box>
                        )}
                        
                        {executionResult.logs && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>{t('Standard Output (Logs):')}</Typography>
                                <Paper elevation={0} sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 2, overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {executionResult.logs}
                                    </pre>
                                </Paper>
                            </Box>
                        )}

                        {executionResult.output !== null && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>{t('Returned Output:')}</Typography>
                                <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.875rem', border: '1px solid', borderColor: 'grey.200' }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(executionResult.output, null, 2)}
                                    </pre>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}
        </Container>
    );
};

export default AdminScripts;
